import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import POPrintModal from '@/pages/manufacturing/production-orders/components/POPrintModal';
import OrderConfirmationSummaryModal from '@/pages/manufacturing/production-orders/components/OrderConfirmationSummaryModal';
import ScheduleCapacityCheck from '@/pages/manufacturing/production-orders/components/ScheduleCapacityCheck';
import RoutingCostPlan from '@/pages/manufacturing/production-orders/components/RoutingCostPlan';
import { money } from '@/pages/masters/routing/stageCost';
import { toInputDate } from '@/utils/format';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import {
  getItemsWithVariantsForBOM,
  type BOMDropdownGroup,
  type BOMDropdownVariant,
} from '@/api/item.api';
import bomAPI, { type BOM } from '@/api/bom.api';
import { getRoutingsForDropdown, type RoutingDropdownItem } from '@/api/routing.api';
import { getAllWarehouses, type WarehouseResponse } from '@/api/warehouse.api';
import { getAllShifts, type ShiftResponse } from '@/api/shift.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { salesOrderService } from '@/services/salesOrderService';
import {
  createProductionOrder,
  updateProductionOrder,
  getProductionOrderById,
  updateProductionOrderStatus,
  getMaterialReservations,
  reserveMaterial,
  releaseMaterial,
  reserveAllMaterials,
  type ProductionOrderPayload,
  type ProductionOrderResponse,
  type MaterialReservation,
  mapApiToProductionOrder,
  getMaterialStockApi,
  completeProductionOrderFlow,
  getStandardCostPlan,
  type StandardCostPlan,
} from '@/api/productionOrder.api';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

type POType = 'MTO' | 'MTS';
type POStatus =
  | 'DRAFT'
  | 'PLANNED'
  | 'CONFIRMED'
  | 'IN_PROGRESS'
  | 'PARTIALLY_DONE'
  | 'COMPLETED'
  | 'ON_HOLD'
  | 'CANCELLED';
type Priority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
type MTSReason = 'Stock Replenishment' | 'Forecast' | 'Safety Stock';

// ─────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────

const statusConfig: Record<
  POStatus,
  { label: string; bg: string; text: string; border: string; icon: string }
> = {
  DRAFT: {
    label: 'Draft',
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    border: 'border-slate-200',
    icon: 'ri-draft-line',
  },
  PLANNED: {
    label: 'Planned',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    icon: 'ri-calendar-line',
  },
  CONFIRMED: {
    label: 'Planned',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    icon: 'ri-checkbox-circle-line',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: 'ri-loader-4-line',
  },
  PARTIALLY_DONE: {
    label: 'Partially Done',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    icon: 'ri-pie-chart-line',
  },
  COMPLETED: {
    label: 'Completed',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    icon: 'ri-check-line',
  },
  ON_HOLD: {
    label: 'On Hold',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    icon: 'ri-pause-circle-line',
  },
  CANCELLED: {
    label: 'Cancelled',
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    icon: 'ri-close-circle-line',
  },
};

const priorityConfig: Record<
  Priority,
  { label: string; bg: string; text: string; dot: string; border: string }
> = {
  LOW: {
    label: 'Low',
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    dot: 'bg-slate-400',
    border: 'border-slate-200',
  },
  NORMAL: {
    label: 'Normal',
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    dot: 'bg-sky-500',
    border: 'border-sky-200',
  },
  HIGH: {
    label: 'High',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    border: 'border-amber-200',
  },
  URGENT: {
    label: 'Urgent',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    dot: 'bg-rose-500',
    border: 'border-rose-200',
  },
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function addWorkingDays(start: string, days: number): string {
  const d = new Date(start);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return d.toISOString().split('T')[0];
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

function generatePONumber(): string {
  const year = new Date().getFullYear();
  const seq = Date.now().toString().slice(-5); // last 5 digits of timestamp
  return `PRD-${year}-${seq}`;
}

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

export default function ProductionOrderForm() {
  const { id: editId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const isEditing = !!editId;

  const navState = (location.state as {
    type?: POType;
    productId?: string;
    productName?: string;
    productCode?: string;
    unit?: string;
    warehouseId?: string;
    bomId?: string;
    plannedQty?: number;
    priority?: Priority;
    notes?: string;
    mtsReason?: MTSReason;
    targetStockLevel?: number;
  } | null);
  const navType = navState?.type;

  // ── Existing PO (edit mode) ──
  const [existingPO, setExistingPO] = useState<ReturnType<typeof mapApiToProductionOrder> | null>(null);
  const [loadingPO, setLoadingPO] = useState(isEditing);

  // ── Remote data ──
  const [itemsList, setItemsList] = useState<BOMDropdownGroup[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [bomsList, setBomsList] = useState<BOM[]>([]);
  const [routingsList, setRoutingsList] = useState<RoutingDropdownItem[]>([]);
  const [loadingRoutings, setLoadingRoutings] = useState(false);
  const [warehousesList, setWarehousesList] = useState<WarehouseResponse[]>([]);
  const [shiftsList, setShiftsList] = useState<ShiftResponse[]>([]);
  const [reservations, setReservations] = useState<MaterialReservation[]>([]);

  // ── Form state ──
  const [poType, setPoType] = useState<POType>(navType || 'MTO');
  const [salesOrderId, setSalesOrderId] = useState('');
  const [salesOrderDisplay, setSalesOrderDisplay] = useState(''); // human-readable: SO-001 - customer
  const [customerName, setCustomerName] = useState('');
  const [salesOrderSearch, setSalesOrderSearch] = useState('');
  const [salesOrderOptions, setSalesOrderOptions] = useState<any[]>([]);
  const [showSalesOrderSearch, setShowSalesOrderSearch] = useState(false);
  const [loadingSalesOrders, setLoadingSalesOrders] = useState(false);
  const [highlightedSalesOrderIndex, setHighlightedSalesOrderIndex] = useState<number>(-1);
  const salesInvoiceId = salesOrderId; // backward compatibility
  const showInvoiceSearch = showSalesOrderSearch;
  const setShowInvoiceSearch = setShowSalesOrderSearch;
  const [mtsReason, setMtsReason] = useState<MTSReason | ''>(navState?.mtsReason || (navType === 'MTS' ? 'Stock Replenishment' : ''));
  const [targetStockLevel, setTargetStockLevel] = useState(navState?.targetStockLevel ? String(navState.targetStockLevel) : '');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(navState?.productId || null);
  const [selectedProduct, setSelectedProduct] = useState<any>(
    navState?.productId || navState?.productName
      ? { id: navState.productId || '', name: navState.productName || '', code: navState.productCode || '', unitName: navState.unit || 'Pcs' }
      : null
  );
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedBOMId, setSelectedBOMId] = useState(navState?.bomId || '');
  const [selectedRoutingId, setSelectedRoutingId] = useState('');
  const [plannedQty, setPlannedQty] = useState(navState?.plannedQty ? String(navState.plannedQty) : '');
  const [unit, setUnit] = useState(navState?.unit || '');
  const [warehouseId, setWarehouseId] = useState(navState?.warehouseId || '');
  const [priority, setPriority] = useState<Priority | ''>(navState?.priority || (navType === 'MTS' ? 'NORMAL' : ''));
  const [plannedStart, setPlannedStart] = useState('');
  const [plannedEnd, setPlannedEnd] = useState('');
  const keepEndDateRef = useRef(false);
  const [shiftId, setShiftId] = useState('');
  const [notes, setNotes] = useState(navState?.notes || '');
  const [saving, setSaving] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [statusOverride, setStatusOverride] = useState<POStatus | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'materials'>('details');
  const [showPrint, setShowPrint] = useState(false);
  const [showMRPBanner, setShowMRPBanner] = useState(false);
  const [bomDetails, setBomDetails] = useState<BOM | null>(null);
  const [loadingBOMDetails, setLoadingBOMDetails] = useState(false);
  const [stockMap, setStockMap] = useState<Record<string, number>>({});
  const [createdSummary, setCreatedSummary] = useState<any | null>(null);

  // ─────────────────────────────────────────────
  // FETCH: Existing PO in edit mode
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!isEditing || !editId) return;
    const fetchPO = async () => {
      try {
        setLoadingPO(true);
        const res = await getProductionOrderById(editId);
        if (res.data) {
          const po = mapApiToProductionOrder(res.data);
          setExistingPO(po);
          // Populate form from fetched PO
          setPoType(po.type as POType);
          setSalesOrderId(po.salesOrderId || po.salesInvoiceId || '');
          if (po.salesOrderId || po.salesInvoiceId) {
            setSalesOrderDisplay(po.salesOrderNumber || po.salesOrderId || po.salesInvoiceId || '');
          }
          setSelectedProductId(po.itemId);
          setSelectedProduct({ id: po.itemId, name: po.itemName, code: po.itemCode, unitName: '' });
          setSelectedBOMId(po.bomId);
          setSelectedRoutingId(po.routingId || '');
          setPlannedQty(po.plannedQty.toString());
          setWarehouseId(po.warehouseId);
          setPriority(po.priority as Priority);
          setPlannedStart(po.plannedStartDate?.split('T')[0] || toInputDate());
          setPlannedEnd(po.plannedEndDate?.split('T')[0] || '');
          setNotes(po.notes || '');
        }
      } catch (err) {
        toast.error('Failed to load production order');
      } finally {
        setLoadingPO(false);
      }
    };
    fetchPO();
  }, [editId, isEditing]);

  // ─────────────────────────────────────────────
  const { selectedWarehouseId } = useWarehouseStore();
  const effectiveWarehouseId = warehouseId || selectedWarehouseId;

  // ─────────────────────────────────────────────
  // FETCH: Items, BOMs, Routings, Warehouses, Shifts
  // ─────────────────────────────────────────────

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoadingItems(true);
        const data = await getItemsWithVariantsForBOM(effectiveWarehouseId || undefined);
        if (data) setItemsList(data);
      } catch {
        toast.error('Failed to load items');
      } finally {
        setLoadingItems(false);
      }
    };
    fetchItems();
  }, [effectiveWarehouseId]);

  useEffect(() => {
    const fetchBOMs = async () => {
      try {
        const res = await bomAPI.getAll({ warehouseId: effectiveWarehouseId || undefined, status: 'ALL', limit: 1000 });
        if (res.data) {
          setBomsList((prev) => {
            const list = [...res.data];
            if (bomDetails && !list.some((b) => b.id === bomDetails.id)) {
              list.push(bomDetails);
            }
            return list;
          });
        }
      } catch {
        console.error('Failed to fetch BOMs');
      }
    };
    fetchBOMs();
  }, [effectiveWarehouseId]);

  useEffect(() => {
    if (!selectedProductId || !effectiveWarehouseId) {
      setRoutingsList([]);
      return;
    }

    let isCancelled = false;
    const fetchRoutings = async () => {
      try {
        setLoadingRoutings(true);
        const data = await getRoutingsForDropdown({
          itemId: selectedProductId,
          warehouseId: effectiveWarehouseId,
        });
        if (!isCancelled) {
          setRoutingsList(data || []);
        }
      } catch {
        if (!isCancelled) {
          console.error('Failed to fetch routings');
          setRoutingsList([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingRoutings(false);
        }
      }
    };
    fetchRoutings();

    return () => {
      isCancelled = true;
    };
  }, [selectedProductId, effectiveWarehouseId]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await getAllWarehouses();
        if (res.data) {
          setWarehousesList(res.data);
          if (!warehouseId) {
            if (navState?.warehouseId && res.data.some((w) => w.id === navState.warehouseId)) {
              setWarehouseId(navState.warehouseId);
            } else if (selectedWarehouseId && res.data.some((w) => w.id === selectedWarehouseId)) {
              setWarehouseId(selectedWarehouseId);
            } else if (res.data.length > 0) {
              setWarehouseId(res.data[0].id);
            }
          }
        }
      } catch {
        console.error('Failed to fetch warehouses');
      }
    };
    fetchWarehouses();
  }, [selectedWarehouseId, isEditing, warehouseId, navState?.warehouseId]);

  useEffect(() => {
    if (!isEditing && !warehouseId && selectedWarehouseId) {
      setWarehouseId(selectedWarehouseId);
    }
  }, [selectedWarehouseId, isEditing, warehouseId]);

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const res = await getAllShifts(effectiveWarehouseId || undefined);
        if (res.data) setShiftsList(res.data);
      } catch {
        console.error('Failed to fetch shifts');
      }
    };
    fetchShifts();
  }, [effectiveWarehouseId]);

  // ─────────────────────────────────────────────
  // FETCH: Material Reservations (edit mode)
  // ─────────────────────────────────────────────

  const fetchReservations = useCallback(async () => {
    if (!isEditing || !editId) return;
    try {
      const res = await getMaterialReservations(editId);
      if (res.data) setReservations(res.data);
    } catch {
      console.error('Failed to fetch reservations');
    }
  }, [editId, isEditing]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // ─────────────────────────────────────────────
  // DERIVED STATE
  // ─────────────────────────────────────────────

  const availableBOMs = useMemo(() => {
    if (!selectedProductId && !selectedBOMId) return [];
    return bomsList.filter(
      (b) =>
        ((selectedProductId && b.product_id === selectedProductId) || (selectedBOMId && b.id === selectedBOMId)) &&
        b.status !== 'OBSOLETE',
    );
  }, [selectedProductId, selectedBOMId, bomsList]);

  // Routings of the selected product (single item or item list) that belong to the order's warehouse or to no warehouse
  const availableRoutings = useMemo(() => {
    if (!selectedProductId) return [];
    return routingsList.filter((r) => {
      if (r.status !== 'ACTIVE') return false;

      const rItemIds: string[] = [];
      if (Array.isArray(r.item_id)) {
        rItemIds.push(...r.item_id);
      } else if (r.item_id) {
        rItemIds.push(...String(r.item_id).split(',').map((s) => s.trim()));
      }
      if (Array.isArray(r.item_ids)) {
        rItemIds.push(...r.item_ids);
      }
      const filteredItemIds = rItemIds.filter(Boolean);
      const matchesItem = filteredItemIds.length === 0 || filteredItemIds.includes(selectedProductId);
      const matchesWarehouse = !effectiveWarehouseId || !r.warehouse_id || r.warehouse_id === effectiveWarehouseId;
      return matchesItem && matchesWarehouse;
    });
  }, [routingsList, selectedProductId, effectiveWarehouseId]);

  // Reset routing selection if current selected routing is not valid for the selected product and warehouse
  useEffect(() => {
    if (selectedRoutingId && !loadingRoutings && !loadingPO) {
      const match = availableRoutings.some((r) => r.id === selectedRoutingId);
      if (!match) {
        setSelectedRoutingId('');
      }
    }
  }, [selectedRoutingId, availableRoutings, loadingRoutings, loadingPO]);

  const selectedRouting = useMemo(
    () => routingsList.find((r) => r.id === selectedRoutingId) || null,
    [selectedRoutingId, routingsList],
  );

  const draftStages = useMemo(() => {
    if (!selectedRouting || !selectedRouting.stages) return [];
    let stages: any[] = [];
    if (Array.isArray(selectedRouting.stages)) {
      stages = selectedRouting.stages;
    } else if (typeof selectedRouting.stages === 'string') {
      try {
        stages = JSON.parse(selectedRouting.stages);
      } catch {
        stages = [];
      }
    }
    return stages.filter((s: any) => String(s?.status || '').toUpperCase() === 'DRAFT');
  }, [selectedRouting]);

  const hasDraftStages = draftStages.length > 0;

  // Standard cost plan of the order as entered: each routing stage's time and cost for the quantity,
  // material, overhead and totals. The backend saves the same plan when the order is confirmed.
  const [costPlan, setCostPlan] = useState<StandardCostPlan | null>(null);
  const [loadingCostPlan, setLoadingCostPlan] = useState(false);
  const [costPlanError, setCostPlanError] = useState<string | null>(null);
  useEffect(() => {
    const qty = parseFloat(plannedQty) || 0;
    if ((!selectedBOMId && !selectedRoutingId) || qty <= 0) {
      setCostPlan(null);
      setCostPlanError(null);
      return;
    }
    let cancelled = false;
    setLoadingCostPlan(true);
    const timer = setTimeout(() => {
      getStandardCostPlan({
        bomId: selectedBOMId || undefined,
        routingId: selectedRoutingId || undefined,
        qty,
        warehouseId: effectiveWarehouseId || undefined,
      })
        .then((res) => {
          if (cancelled) return;
          setCostPlan(res.success ? res.data : null);
          setCostPlanError(res.success ? null : res.message || 'Could not calculate the standard cost');
        })
        .catch((err) => {
          if (!cancelled) setCostPlanError(err instanceof Error ? err.message : 'Could not calculate the standard cost');
        })
        .finally(() => { if (!cancelled) setLoadingCostPlan(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [selectedBOMId, selectedRoutingId, plannedQty, effectiveWarehouseId]);

  // Rate of each material in the plan, for the requirements table
  const planRateByItem = useMemo(
    () => new Map((costPlan?.materials || []).map((m) => [String(m.itemId), m.rate])),
    [costPlan],
  );


  const selectedBOM = useMemo(
    () => bomsList.find((b) => b.id === selectedBOMId) || null,
    [selectedBOMId, bomsList],
  );

  const selectedWarehouse = useMemo(
    () => warehousesList.find((w) => w.id === warehouseId) || null,
    [warehouseId, warehousesList],
  );

  const bomOptions: SearchableOption[] = useMemo(() => {
    return availableBOMs.map((bom) => ({
      value: bom.id,
      label: `v${bom.version} (${bom.status}) — ${bom.code || bom.product_name || ''}`,
      badge: bom.status,
    }));
  }, [availableBOMs]);

  const routingOptions: SearchableOption[] = useMemo(() => {
    const list: SearchableOption[] = [{ value: '', label: 'No routing' }];
    availableRoutings.forEach((routing) => {
      let stageCount = 0;
      if (routing.stages) {
        if (Array.isArray(routing.stages)) {
          stageCount = routing.stages.length;
        } else if (typeof routing.stages === 'string') {
          try {
            stageCount = JSON.parse(routing.stages).length;
          } catch {
            stageCount = 0;
          }
        }
      }
      list.push({
        value: routing.id,
        label: `${routing.name} (${routing.code})`,
        sublabel: `${stageCount} stages · ${routing.total_time_minutes || 0} min`,
      });
    });
    return list;
  }, [availableRoutings]);

  const warehouseOptions: SearchableOption[] = useMemo(() => {
    return warehousesList.map((w) => ({
      value: w.id,
      label: w.name,
      sublabel: w.prefix || w.type || '',
    }));
  }, [warehousesList]);

  const priorityOptions: SearchableOption[] = useMemo(() => {
    return (['LOW', 'NORMAL', 'HIGH', 'URGENT'] as Priority[]).map((p) => ({
      value: p,
      label: priorityConfig[p].label,
    }));
  }, []);

  const shiftOptions: SearchableOption[] = useMemo(() => {
    const filteredShifts = warehouseId
      ? shiftsList.filter((s) => !s.warehouse_id || s.warehouse_id === warehouseId)
      : shiftsList;
    return [
      { value: '', label: 'No shift assigned' },
      ...filteredShifts.map((s) => ({
        value: s.id,
        label: `${s.name} (${s.start_time}–${s.end_time})`,
      })),
    ];
  }, [shiftsList, warehouseId]);

  // Reset shift if it does not belong to the selected warehouse
  useEffect(() => {
    if (shiftId && warehouseId && shiftsList.length > 0) {
      const match = shiftsList.find((s) => s.id === shiftId);
      if (match && match.warehouse_id && match.warehouse_id !== warehouseId) {
        setShiftId('');
      }
    }
  }, [warehouseId, shiftId, shiftsList]);

  const mtsReasonOptions: SearchableOption[] = useMemo(() => [
    { value: 'Stock Replenishment', label: 'Stock Replenishment' },
    { value: 'Forecast', label: 'Forecast' },
    { value: 'Safety Stock', label: 'Safety Stock' },
  ], []);

  // Helper: Only allow Finished Goods and Semi-Finished items (exclude raw materials, consumables, etc.)
  const isFinishedOrSemi = useCallback((itemType?: string, category?: string) => {
    const raw = String(itemType || category || '').toUpperCase().trim();
    if (!raw) return true; // Include if unspecified
    const clean = raw.replace(/[\s_-]+/g, '');
    if (clean.includes('RAW') || clean.includes('CONSUMABLE') || clean.includes('PACKAGING') || clean.includes('SERVICE') || clean.includes('ASSET')) {
      return false;
    }
    return clean.includes('FINISHED') || clean.includes('SEMI');
  }, []);

  // Map of products with created BOMs
  const bomProductMap = useMemo(() => {
    const map = new Map<string, { hasBom: boolean; bomCount: number; activeBom?: BOM }>();
    bomsList.forEach((b) => {
      if (b.product_id) {
        const existing = map.get(b.product_id) || { hasBom: true, bomCount: 0 };
        existing.bomCount += 1;
        if (b.status === 'ACTIVE' || !existing.activeBom) existing.activeBom = b;
        map.set(b.product_id, existing);
      }
    });
    return map;
  }, [bomsList]);

  // Checker whether an item has a BOM created
  const checkHasBOM = useCallback((itemId?: string | null, itemCode?: string | null) => {
    if (!itemId && !itemCode) return false;
    if (itemId && bomProductMap.has(itemId)) return true;
    if (itemCode && bomsList.some((b) => b.product_code && b.product_code.toLowerCase() === itemCode.toLowerCase())) {
      return true;
    }
    return false;
  }, [bomProductMap, bomsList]);

  const productDropdownData = useMemo(() => {
    const q = productSearchQuery.toLowerCase();
    // 1. Filter out raw materials & consumables, only keep Finished Goods & Semi-Finished
    const allowedGroups = itemsList.filter((group) => {
      const groupOk = isFinishedOrSemi(group.item_type, group.category);
      const hasOkVariant = group.variants.some((v) =>
        isFinishedOrSemi((v as any).item_type || group.item_type, (v as any).category || group.category)
      );
      return groupOk || hasOkVariant;
    });

    let filtered = allowedGroups;
    if (q) {
      filtered = filtered.filter((group) => {
        const matchesItem =
          group.name.toLowerCase().includes(q) ||
          (group.code && group.code.toLowerCase().includes(q));
        const matchesVariant = group.variants.some(
          (v) =>
            v.name.toLowerCase().includes(q) ||
            (v.code && v.code.toLowerCase().includes(q)),
        );
        return matchesItem || matchesVariant;
      });
    }
    const parents: BOMDropdownGroup[] = [];
    const variants: BOMDropdownVariant[] = [];
    const regulars: BOMDropdownGroup[] = [];
    filtered.forEach((group) => {
      const validVariants = group.variants.filter((v) =>
        isFinishedOrSemi((v as any).item_type || group.item_type, (v as any).category || group.category)
      );
      if (validVariants.length > 0) {
        parents.push({ ...group, variants: validVariants });
        variants.push(...validVariants);
      } else if (isFinishedOrSemi(group.item_type, group.category)) {
        regulars.push(group);
      }
    });
    return { parents, variants, regulars };
  }, [itemsList, productSearchQuery, isFinishedOrSemi]);

  // ─────────────────────────────────────────────
  // SIDE EFFECTS
  // ─────────────────────────────────────────────

  // Auto-calc end date from the time the quantity takes on the routing (8 working hours a day);
  // the capacity check below refines it per work center
  useEffect(() => {
    if (!plannedStart || !selectedRouting) return;
    // Dates picked from the capacity check keep their end date
    if (keepEndDateRef.current) { keepEndDateRef.current = false; return; }
    const totalMinutes = costPlan ? costPlan.totals.plannedMinutes : (selectedRouting.total_time_minutes || 0);
    const days = Math.ceil(totalMinutes / 60 / 8);
    const calcEnd = addWorkingDays(plannedStart, Math.max(days, 1));
    if (!isEditing || !plannedEnd) setPlannedEnd(calcEnd);
  }, [plannedStart, selectedRouting, costPlan]);

  // Sync unit from selected product
  useEffect(() => {
    if (selectedProduct?.unitName) setUnit(selectedProduct.unitName);
  }, [selectedProduct]);

  // Auto-select BOM if available for product
  useEffect(() => {
    if (!selectedBOMId && availableBOMs.length > 0) {
      const activeBom = availableBOMs.find((b) => b.status === 'ACTIVE') || availableBOMs[0];
      setSelectedBOMId(activeBom.id);
    }
  }, [availableBOMs, selectedBOMId]);

  // Auto-sync Routing with selected product
  useEffect(() => {
    if (selectedRoutingId) {
      const stillValid = availableRoutings.some((r) => r.id === selectedRoutingId);
      if (!stillValid) {
        setSelectedRoutingId('');
      }
    } else if (!isEditing && availableRoutings.length > 0) {
      // Auto-select routing linked to this product (prioritizing specific item match)
      const specific = availableRoutings.find((r) => {
        const ids = [
          ...(Array.isArray(r.item_id) ? r.item_id : (r.item_id ? String(r.item_id).split(',') : [])),
          ...(Array.isArray(r.item_ids) ? r.item_ids : [])
        ].map((s) => String(s).trim());
        return selectedProductId && ids.includes(selectedProductId);
      }) || availableRoutings[0];
      if (specific) {
        setSelectedRoutingId(specific.id);
      }
    }
  }, [availableRoutings, selectedRoutingId, isEditing, selectedProductId]);

  // Sync selectedProduct with itemsList if navState provided productId
  useEffect(() => {
    if (navState?.productId && itemsList.length > 0) {
      for (const group of itemsList) {
        if (group.id === navState.productId) {
          setSelectedProduct((prev: any) => ({
            ...prev,
            id: group.id,
            name: group.name,
            code: group.code,
            unitName: prev?.unitName || '',
          }));
          break;
        }
        const variant = group.variants.find((v) => v.id === navState.productId);
        if (variant) {
          setSelectedProduct((prev: any) => ({
            ...prev,
            id: variant.id,
            name: variant.name,
            code: variant.code,
            unitName: prev?.unitName || '',
          }));
          break;
        }
      }
    }
  }, [itemsList, navState?.productId]);

  // Resolve BOM and Product details if navigated with bomId
  useEffect(() => {
    if (navState?.bomId && (!selectedProductId || !selectedProduct?.name)) {
      bomAPI.getById(navState.bomId)
        .then((bom) => {
          if (bom) {
            if (bom.product_id && !selectedProductId) {
              setSelectedProductId(bom.product_id);
            }
            setSelectedProduct((prev: any) => ({
              id: bom.product_id || prev?.id || '',
              name: bom.product_name || prev?.name || 'Product',
              code: bom.product_code || prev?.code || '',
              unitName: bom.product_uom || bom.unit_name || prev?.unitName || 'Pcs',
            }));
            if (bom.product_uom || bom.unit_name) {
              setUnit((prev) => prev || bom.product_uom || bom.unit_name || 'Pcs');
            }
            if (bom.warehouse_id && !warehouseId) {
              setWarehouseId(bom.warehouse_id);
            }
            setBomsList((prev) => {
              if (prev.some((b) => b.id === bom.id)) return prev;
              return [bom, ...prev];
            });
          }
        })
        .catch((err) => console.error('Failed to resolve BOM for Production Order:', err));
    }
  }, [navState?.bomId, selectedProductId, selectedProduct?.name, warehouseId]);

  // Fetch detailed BOM items when selectedBOMId changes
  useEffect(() => {
    if (!selectedBOMId) {
      setBomDetails(null);
      return;
    }
    let isCurrent = true;
    const fetchBOM = async () => {
      try {
        setLoadingBOMDetails(true);
        const data = await bomAPI.getById(selectedBOMId);
        if (isCurrent && data) {
          setBomDetails(data);
          setBomsList((prev) => {
            if (prev.some((b) => b.id === data.id)) return prev;
            return [data, ...prev];
          });
          if (!selectedProductId && data.product_id) {
            setSelectedProductId(data.product_id);
          }
          if (data.product_name && (!selectedProduct || !selectedProduct.name)) {
            setSelectedProduct((prev: any) => ({
              id: data.product_id || prev?.id || '',
              name: data.product_name,
              code: data.product_code || prev?.code || '',
              unitName: data.product_uom || data.unit_name || prev?.unitName || 'Pcs',
            }));
          }
          if (data.product_uom || data.unit_name) {
            setUnit((prev) => prev || data.product_uom || data.unit_name || 'Pcs');
          }
          if (data.warehouse_id && !warehouseId) {
            setWarehouseId(data.warehouse_id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch BOM details:', err);
      } finally {
        if (isCurrent) setLoadingBOMDetails(false);
      }
    };
    fetchBOM();
    return () => {
      isCurrent = false;
    };
  }, [selectedBOMId, selectedProductId, warehouseId, selectedProduct]);

  // Fetch warehouse stock quantities for materials originally from stock
  useEffect(() => {
    let isCurrent = true;
    const fetchStock = async () => {
      try {
        const itemIds = bomDetails?.items?.map((it: any) => it.item_id || it.id).filter(Boolean);
        const res = await getMaterialStockApi({
          warehouseId: warehouseId || undefined,
          itemIds: itemIds && itemIds.length > 0 ? itemIds : undefined,
        });
        if (isCurrent && res && res.success && res.data) {
          const map: Record<string, number> = {};
          if ((res.data as any).stockMap) {
            Object.assign(map, (res.data as any).stockMap);
          } else if (Array.isArray(res.data)) {
            res.data.forEach((s: any) => {
              const qty = Number(s.available_stock ?? s.total_stock ?? s.quantity ?? 0);
              map[s.item_id || s.id] = qty;
              if (s.item_code || s.code) map[s.item_code || s.code] = qty;
            });
          }
          setStockMap(map);
        }
      } catch (err) {
        console.error('Failed to fetch stock for material requirements:', err);
      }
    };
    fetchStock();
    return () => {
      isCurrent = false;
    };
  }, [warehouseId, bomDetails]);

  // ─────────────────────────────────────────────
  // DYNAMIC MATERIAL REQUIREMENTS CALCULATION
  // Recalculates dynamically when plannedQty is entered/changed
  // ─────────────────────────────────────────────
  const materialRequirements = useMemo(() => {
    const qtyToProduce = Math.max(0, parseFloat(plannedQty) || 0);

    // 1. If BOM details items are available:
    if (bomDetails?.items && bomDetails.items.length > 0) {
      return bomDetails.items.map((item: any) => {
        const qtyPerUnit = parseFloat(String(item.quantity)) || 1;
        const scrapPct = parseFloat(String(item.scrap_percentage)) || 0;
        const scrapFactor = 1 + scrapPct / 100;
        const requiredQty = (qtyToProduce > 0 ? qtyToProduce : 1) * qtyPerUnit * scrapFactor;

        // Fetch stock originally from stockMap (by item_id or item_code) or BOM stock_available
        let availableQty = 0;
        if (stockMap[item.item_id] !== undefined) {
          availableQty = Number(stockMap[item.item_id]);
        } else if (item.item_code && stockMap[item.item_code] !== undefined) {
          availableQty = Number(stockMap[item.item_code]);
        } else if (item.stock_available !== undefined && item.stock_available !== null) {
          availableQty = Number(item.stock_available);
        } else if (item.total_stock !== undefined && item.total_stock !== null) {
          availableQty = Number(item.total_stock);
        }

        const isSufficient = availableQty >= requiredQty;

        return {
          id: item.id || item.item_id,
          itemId: item.item_id,
          itemName: item.item_name || 'Material Item',
          itemCode: item.item_code || '',
          requiredQty,
          availableQty,
          unit: item.unit_name || item.unit || 'Pcs',
          isSufficient,
        };
      });
    }

    // 2. If reservations are loaded (e.g. edit mode)
    if (reservations.length > 0) {
      const originalPOQty = Number(existingPO?.plannedQty) || 1;
      const ratio = qtyToProduce > 0 ? qtyToProduce / originalPOQty : 1;

      return reservations.map((mr) => {
        const requiredQty = Number(mr.requiredQty || 0) * (qtyToProduce > 0 ? ratio : 1);
        let availableQty = 0;
        if (stockMap[mr.itemId] !== undefined) {
          availableQty = Number(stockMap[mr.itemId]);
        } else if (mr.itemCode && stockMap[mr.itemCode] !== undefined) {
          availableQty = Number(stockMap[mr.itemCode]);
        }

        const isSufficient = availableQty >= requiredQty;

        return {
          id: mr.id,
          itemId: mr.itemId,
          itemName: mr.itemName,
          itemCode: mr.itemCode,
          requiredQty,
          availableQty,
          unit: mr.unit,
          isSufficient,
        };
      });
    }

    return [];
  }, [bomDetails, reservations, plannedQty, stockMap, existingPO]);

  // ─────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────

  const handleSelectProduct = useCallback(
    (productId: string, selectedItem?: BOMDropdownGroup | BOMDropdownVariant) => {
      let product: any = selectedItem || itemsList.find((m) => m.id === productId);
      if (!product) return;

      let productName = product.name;
      let productCode = product.code;
      let productUnitName = product.unit_name || 'Pcs';

      if ('variant_name' in product && product.variant_name) {
        productName = `${product.parent_item_name} - ${product.variant_name}`;
        productCode = product.code;
        productUnitName = product.unit_name || 'Pcs';
      }

      setSelectedProductId(productId);
      setSelectedProduct({
        id: product.id,
        name: productName,
        code: productCode,
        unitName: productUnitName,
        isVariant: 'variant_name' in product,
        parentItemId: 'parent_item_id' in product ? product.parent_item_id : null,
        variantName: 'variant_name' in product ? product.variant_name : null,
      });

      setShowProductSearch(false);
      setProductSearchQuery('');
      setUnit(productUnitName);

      // Auto-select active BOM for this product if available
      const activeBom = bomsList.find((b) => b.product_id === productId && b.status === 'ACTIVE') ||
                        bomsList.find((b) => b.product_id === productId);
      if (activeBom) {
        setSelectedBOMId(activeBom.id);
      } else {
        setSelectedBOMId('');
      }

      // Auto-select routing linked to this product if available
      const linkedRouting = routingsList.find((r) => {
        if (r.status !== 'ACTIVE') return false;
        const ids = [
          ...(Array.isArray(r.item_id) ? r.item_id : (r.item_id ? String(r.item_id).split(',') : [])),
          ...(Array.isArray(r.item_ids) ? r.item_ids : [])
        ].map((s) => String(s).trim());
        return ids.includes(productId);
      });
      if (linkedRouting) {
        setSelectedRoutingId(linkedRouting.id);
      } else {
        setSelectedRoutingId('');
      }
    },
    [itemsList, bomsList, routingsList],
  );

  const handleSalesOrderSearch = useCallback(async (query: string) => {
    setSalesOrderSearch(query);
    setHighlightedSalesOrderIndex(-1);
    try {
      setLoadingSalesOrders(true);
      const res = await salesOrderService.listSalesOrders({ search: query, limit: 30 });
      if (Array.isArray(res)) setSalesOrderOptions(res);
    } catch {
      console.error('Failed to search sales orders');
    } finally {
      setLoadingSalesOrders(false);
    }
  }, []);

  const handleSelectSalesOrder = useCallback(
    async (order: any) => {
      setSalesOrderId(order.id);
      const soNum = order.soNumber || order.so_number || order.id;
      const cust = order.customerName || order.customer_name || '';
      setSalesOrderDisplay(`${soNum} — ${cust}`);
      setCustomerName(cust);
      setSalesOrderSearch('');
      setSalesOrderOptions([]);
      setShowSalesOrderSearch(false);
      setHighlightedSalesOrderIndex(-1);

      try {
        const orderData = await salesOrderService.getSalesOrder(order.id).catch(() => order);
        if (orderData) {
          const party = orderData.customerName || orderData.customer_name;
          if (party) setCustomerName(party);
          const wh = orderData.warehouseId || orderData.warehouse_id;
          if (wh) setWarehouseId(wh);

          const items = orderData.items || [];
          if (items.length > 0) {
            const mainItem = items[0];
            const pId = mainItem.itemId || mainItem.item_id;
            if (pId) {
              setSelectedProductId(pId);
              const found = itemsList.find((it) => it.id === pId);
              setSelectedProduct({
                id: pId,
                name: mainItem.itemName || mainItem.item_name || found?.name || 'Product',
                code: mainItem.itemCode || mainItem.code || found?.code || '',
                unitName: mainItem.unit || found?.unitName || 'Pcs',
              });
              setUnit(mainItem.unit || found?.unitName || 'Pcs');
              const itemQty = mainItem.orderedQty ?? mainItem.ordered_qty ?? mainItem.qty ?? mainItem.quantity;
              if (itemQty) {
                setPlannedQty(Number(itemQty).toString());
              }
              handleSelectProduct(pId, found);
            }
          }
          toast.success(`Mapped details from Sales Order ${soNum}`);
        }
      } catch (err) {
        console.error('Failed to fetch sales order details for auto-mapping:', err);
      }
    },
    [toast, itemsList, handleSelectProduct],
  );

  const handleSalesOrderKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSalesOrderSearch) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedSalesOrderIndex((prev) =>
          prev < salesOrderOptions.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedSalesOrderIndex((prev) =>
          prev > 0 ? prev - 1 : salesOrderOptions.length - 1,
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (
          highlightedSalesOrderIndex >= 0 &&
          highlightedSalesOrderIndex < salesOrderOptions.length
        ) {
          handleSelectSalesOrder(salesOrderOptions[highlightedSalesOrderIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowSalesOrderSearch(false);
      }
    },
    [showSalesOrderSearch, salesOrderOptions, highlightedSalesOrderIndex, handleSelectSalesOrder],
  );

  const handleClearSalesOrder = useCallback(() => {
    setSalesOrderId('');
    setSalesOrderDisplay('');
    setCustomerName('');
    setSalesOrderSearch('');
    setSalesOrderOptions([]);
    setHighlightedSalesOrderIndex(-1);
  }, []);

  const handleInvoiceSearch = handleSalesOrderSearch;
  const handleSelectInvoice = handleSelectSalesOrder;
  const handleInvoiceKeyDown = handleSalesOrderKeyDown;
  const handleClearInvoice = handleClearSalesOrder;

  const handleSave = useCallback(async (targetStatus: 'DRAFT' | 'PLANNED' = 'DRAFT') => {
    if (!selectedProductId) return toast.error('Please select a product');
    if (selectedProduct?.item_type && selectedProduct.item_type.toUpperCase().includes('RAW')) {
      return toast.error('Raw materials cannot be manufactured directly. Please select a Finished Good.');
    }
    if (!selectedBOMId) {
      return toast.error('Active BOM is required for this product before creating a Production Order.');
    }
    if (!plannedQty || parseFloat(plannedQty) <= 0) {
      return toast.error('Planned quantity must be greater than 0');
    }
    const finalWarehouseId = warehouseId || selectedWarehouseId;
    if (!finalWarehouseId) return toast.error('Please select a warehouse from the top bar');
    if (!priority) return toast.error('Please select order priority');
    if (poType === 'MTS' && !mtsReason) return toast.error('Please select stock trigger reason');
    if (!plannedStart) return toast.error('Please select planned start date');
    if (!plannedEnd) return toast.error('Please select planned end date');
    if (new Date(plannedStart) >= new Date(plannedEnd)) {
      return toast.error('Planned Start date must be earlier than Planned End date');
    }
    if (hasDraftStages) {
      const stageName = draftStages[0]?.operationName || draftStages[0]?.stageName || draftStages[0]?.name || `Stage ${draftStages[0]?.sequence || 1}`;
      return toast.error(`Stage '${stageName}' is in DRAFT — complete BOM mapping first.`);
    }

    setSaving(true);
    try {
      const payload: ProductionOrderPayload = {
        poNumber: isEditing ? existingPO!.poNumber : undefined,
        type: poType,
        status: targetStatus,
        priority: (priority || 'NORMAL') as Priority,
        itemId: selectedProductId,
        bomId: selectedBOMId || undefined,
        plannedQty: parseFloat(plannedQty),
        plannedStartDate: plannedStart,
        plannedEndDate: plannedEnd,
        warehouseId: finalWarehouseId,
        routingId: selectedRoutingId || undefined,
        shiftId: shiftId || undefined,
        salesOrderId: poType === 'MTO' ? salesOrderId || undefined : undefined,
        notes: notes || undefined,
      };

      if (isEditing && editId) {
        await updateProductionOrder(editId, payload);

        // Apply status override if set
        if (statusOverride) {
          // Completion posts finished goods, cost and accounting, so it runs the full completion flow
          if (statusOverride === 'COMPLETED') await completeProductionOrderFlow(editId);
          else await updateProductionOrderStatus(editId, statusOverride);
        }

        toast.success(`Production Order ${existingPO?.poNumber} updated`);
        navigate('/manufacturing/production-orders');
      } else {
        const res: any = await createProductionOrder(payload);
        if (res && res.success) {
          const poId = res.prd_id || res.data?.id;
          const poNum = res.data?.po_number || res.prd_number || '';
          if (targetStatus === 'DRAFT') {
            setCreatedSummary(null);
            toast.success(`Production Order ${poNum} saved as Draft`);
            navigate(`/manufacturing/production-orders/${poId}`);
          } else {
            setCreatedSummary(res);
            toast.success(`Production Order ${poNum} created successfully`);
          }
        } else {
          toast.success(`Production Order created`);
          navigate('/manufacturing/production-orders');
        }
      }

      setShowMRPBanner(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save production order');
    } finally {
      setSaving(false);
    }
  }, [
    selectedProductId,
    selectedBOMId,
    plannedQty,
    warehouseId,
    plannedStart,
    plannedEnd,
    poType,
    priority,
    mtsReason,
    salesInvoiceId,
    selectedRoutingId,
    shiftId,
    notes,
    isEditing,
    existingPO,
    editId,
    statusOverride,
    toast,
    navigate,
  ]);

  const handleStatusAction = useCallback(
    (nextStatus: POStatus) => {
      setStatusOverride(nextStatus);
      toast.info(`Status will change to ${statusConfig[nextStatus].label} on save`);
    },
    [toast],
  );

  const handleReserveOne = useCallback(
    async (mr: MaterialReservation) => {
      if (!editId) return;
      try {
        setReservations((prev) =>
          prev.map((item) =>
            item.id === mr.id || item.itemId === mr.itemId
              ? { ...item, reservedQty: item.requiredQty, status: 'RESERVED' as const }
              : item
          )
        );
        await reserveMaterial(editId, mr.id);
        toast.success(`Reserved ${mr.itemName}`);
        fetchReservations();
      } catch (err: any) {
        toast.error(err?.message || `Failed to reserve ${mr.itemName}`);
        fetchReservations();
      }
    },
    [editId, fetchReservations, toast],
  );

  const handleReleaseOne = useCallback(
    async (mr: MaterialReservation) => {
      if (!editId) return;
      try {
        setReservations((prev) =>
          prev.map((item) =>
            item.id === mr.id || item.itemId === mr.itemId
              ? { ...item, reservedQty: 0, status: 'PENDING' as const }
              : item
          )
        );
        await releaseMaterial(editId, mr.id);
        toast.success(`Released ${mr.itemName}`);
        fetchReservations();
      } catch (err: any) {
        toast.error(err?.message || `Failed to release ${mr.itemName}`);
        fetchReservations();
      }
    },
    [editId, fetchReservations, toast],
  );

  const handleReserveAll = useCallback(async () => {
    if (!editId) return;
    try {
      setReservations((prev) =>
        prev.map((item) => ({
          ...item,
          reservedQty: item.requiredQty,
          status: 'RESERVED' as const,
        }))
      );
      await reserveAllMaterials(editId);
      toast.success('Reserved all available materials');
      fetchReservations();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reserve materials');
      fetchReservations();
    }
  }, [editId, fetchReservations, toast]);

  // ─────────────────────────────────────────────
  // DERIVED UI STATE
  // ─────────────────────────────────────────────

  const currentStatus: POStatus =
    statusOverride || (isEditing && existingPO ? (existingPO.status as POStatus) : 'DRAFT');

  const statusActions = useMemo(() => {
    const actions: Array<{
      label: string;
      icon: string;
      next: POStatus;
      variant: string;
    }> = [];
    if (currentStatus === 'DRAFT')
      actions.push({
        label: 'Submit for Planning',
        icon: 'ri-send-plane-line',
        next: 'PLANNED',
        variant: 'bg-sky-600 hover:bg-sky-700 text-white',
      });
    if (currentStatus === 'PLANNED')
      actions.push({
        label: 'Start Production',
        icon: 'ri-play-line',
        next: 'IN_PROGRESS',
        variant: 'bg-amber-600 hover:bg-amber-700 text-white',
      });
    if (currentStatus === 'IN_PROGRESS') {
      actions.push({
        label: 'Complete',
        icon: 'ri-check-line',
        next: 'COMPLETED',
        variant: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      });
      actions.push({
        label: 'Put on Hold',
        icon: 'ri-pause-line',
        next: 'ON_HOLD',
        variant: 'bg-rose-600 hover:bg-rose-700 text-white',
      });
    }
    if (currentStatus === 'ON_HOLD') {
      actions.push({
        label: 'Resume',
        icon: 'ri-play-line',
        next: 'IN_PROGRESS',
        variant: 'bg-amber-600 hover:bg-amber-700 text-white',
      });
      actions.push({
        label: 'Cancel',
        icon: 'ri-close-line',
        next: 'CANCELLED',
        variant: 'bg-slate-600 hover:bg-slate-700 text-white',
      });
    }
    return actions;
  }, [currentStatus]);

  const showTabs =
    isEditing &&
    existingPO &&
    (existingPO.status === 'PLANNED' || existingPO.status === 'IN_PROGRESS');


  console.log('selectedRoutingId:', selectedRoutingId);
  console.log('selectedRouting:', selectedRouting);
  // ─────────────────────────────────────────────
  // LOADING STATE
  // ─────────────────────────────────────────────

  if (loadingPO) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-[#64748b]">
            <i className="ri-loader-4-line animate-spin text-[#4f46e5]" />
            Loading production order...
          </div>
        </div>
      </AppLayout>
    );
  }

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
            <div>
              <h1 className="text-xl font-bold text-[#1e293b]">
                {isEditing
                  ? `Edit Production Order — ${existingPO?.poNumber}`
                  : 'New Production Order'}
              </h1>
              <p className="text-xs text-[#64748b] mt-0.5">
                {isEditing
                  ? 'Update production order details and status'
                  : 'Create a new Make-to-Order or Make-to-Stock production order'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isEditing && existingPO && (
                <button
                  type="button"
                  onClick={() => setShowPrint(true)}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-[#64748b] hover:bg-white cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-printer-line" />
                  Print
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/manufacturing/production-orders')}
                className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-[#64748b] hover:bg-white cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              {!isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={() => void handleSave('DRAFT')}
                    disabled={saving || hasDraftStages}
                    title={hasDraftStages ? 'Routing has DRAFT stages — complete BOM mapping first' : undefined}
                    className="h-9 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-2xs"
                  >
                    <i className="ri-draft-line text-slate-500" />
                    Save as Draft
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSave('PLANNED')}
                    disabled={saving || hasDraftStages}
                    title={hasDraftStages ? 'Routing has DRAFT stages — complete BOM mapping first' : undefined}
                    className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-[#4338ca] disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-xs"
                  >
                    <i className="ri-checkbox-circle-line" />
                    Confirm Order
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving || hasDraftStages}
                  title={hasDraftStages ? 'Routing has DRAFT stages — complete BOM mapping first' : undefined}
                  className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-[#4338ca] disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line" /> Save Order
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          {showTabs && (
            <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-5 w-fit">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${activeTab === 'details' ? 'bg-[#4f46e5] text-white' : 'text-[#64748b] hover:bg-slate-50'}`}
              >
                <i className="ri-file-list-3-line mr-1.5" />
                Details
              </button>
              <button
                onClick={() => setActiveTab('materials')}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${activeTab === 'materials' ? 'bg-[#4f46e5] text-white' : 'text-[#64748b] hover:bg-slate-50'}`}
              >
                <i className="ri-box-3-line mr-1.5" />
                Materials
              </button>
            </div>
          )}

          {/* ── DETAILS TAB ── */}
          {activeTab === 'details' && (
            <>
              {/* Type Selector */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-2">
                  Order Type
                </label>
                <div className="flex gap-2">
                  {(['MTO', 'MTS'] as POType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setPoType(t)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${poType === t ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'bg-white text-[#475569] border-slate-200 hover:border-slate-300'}`}
                    >
                      <i className={t === 'MTO' ? 'ri-building-line' : 'ri-box-3-line'} />
                      {t === 'MTO' ? 'Make to Order (MTO)' : 'Make to Stock (MTS)'}
                    </button>
                  ))}
                </div>
              </div>

              {/* MTO Fields */}
              {poType === 'MTO' && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                  <h3 className="text-sm font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-[#4f46e5]" />
                    Sales Order Link
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Sales Order Searchable & Scrollable Dropdown */}
                    <div className="relative" onKeyDown={handleSalesOrderKeyDown}>
                      <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                        Sales Order <span className="text-[#94a3b8] font-normal">(Optional)</span>
                      </label>
                      {salesOrderId ? (
                        <div className="flex items-center gap-2 h-10 px-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                          <i className="ri-file-text-line text-indigo-600 shrink-0 text-base" />
                          <span className="text-sm font-semibold text-[#1e293b] flex-1 truncate">
                            {salesOrderDisplay}
                          </span>
                          <button
                            type="button"
                            onClick={handleClearSalesOrder}
                            className="text-slate-400 hover:text-slate-600 shrink-0 p-1 cursor-pointer"
                            title="Clear selected sales order"
                          >
                            <i className="ri-close-line text-base" />
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <input
                            type="text"
                            value={salesOrderSearch}
                            onChange={(e) => {
                              handleSalesOrderSearch(e.target.value);
                              setShowSalesOrderSearch(true);
                            }}
                            onFocus={() => {
                              setShowSalesOrderSearch(true);
                              handleSalesOrderSearch(salesOrderSearch);
                            }}
                            placeholder="Type to search or click to view sales orders..."
                            className="w-full h-10 px-3 pr-8 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                          />
                          <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                          {showSalesOrderSearch && (
                            <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100">
                              {loadingSalesOrders ? (
                                <div className="px-4 py-3 text-xs text-slate-400 flex items-center gap-2">
                                  <i className="ri-loader-4-line animate-spin text-indigo-600 text-sm" /> Searching Sales Orders...
                                </div>
                              ) : salesOrderOptions.length > 0 ? (
                                salesOrderOptions.map((order, index) => (
                                  <button
                                    key={order.id}
                                    type="button"
                                    onClick={() => handleSelectSalesOrder(order)}
                                    className={`w-full px-4 py-2.5 text-left transition-colors cursor-pointer flex items-center justify-between gap-3 ${index === highlightedSalesOrderIndex
                                        ? 'bg-indigo-100 text-indigo-900'
                                        : 'hover:bg-indigo-50 text-slate-800'
                                      }`}
                                  >
                                    <div>
                                      <p className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                                        <i className="ri-file-text-line text-indigo-500" />
                                        {order.soNumber || order.so_number || order.id}
                                      </p>
                                      <p className="text-[11px] text-slate-500 mt-0.5">{order.customerName || order.customer_name || 'Customer'}</p>
                                    </div>
                                    {(order.date || order.expectedDeliveryDate) && (
                                      <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 shrink-0">
                                        {order.date || order.expectedDeliveryDate}
                                      </span>
                                    )}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-xs text-slate-400 text-center">
                                  No sales orders found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-[11px] text-[#94a3b8] mt-1">
                        Search via keyboard or scroll list to auto-map Customer, Product, Quantity & Warehouse
                      </p>
                    </div>
                    {/* Customer (auto-filled) */}
                    <div>
                      <label className="block text-xs font-medium text-[#64748b] mb-1">
                        Customer Name
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        readOnly={!!salesInvoiceId}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Auto-filled from invoice"
                        className={`w-full h-10 px-3 text-sm border rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100 ${salesInvoiceId ? 'bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-200'}`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* MTS Fields */}
              {poType === 'MTS' && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                  <h3 className="text-sm font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
                    <i className="ri-box-3-line text-[#059669]" />
                    Stock Trigger
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#64748b] mb-1">
                        Reason
                      </label>
                      <SearchableSelect
                        value={mtsReason}
                        onChange={(e) => setMtsReason(e as MTSReason)}
                        options={mtsReasonOptions}
                        placeholder="Select Reason"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#64748b] mb-1">
                        Target Stock Level
                      </label>
                      <input
                        type="number"
                        value={targetStockLevel}
                        onChange={(e) => setTargetStockLevel(e.target.value)}
                        placeholder="Produce until stock reaches this level"
                        className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Product Details */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                <h3 className="text-sm font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
                  <i className="ri-archive-line text-[#4f46e5]" />
                  Product Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Product Selector */}
                  <div className="md:col-span-2 relative">
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      Product <span className="text-red-500">*</span>
                    </label>
                    <div
                      className={`w-full min-h-[42px] px-3 py-2 text-sm border rounded-lg cursor-pointer flex items-center justify-between ${selectedProduct ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                      onClick={() => { if (!isEditing) setShowProductSearch(true); }}
                    >
                      <div className="flex items-center gap-2 flex-wrap flex-1 mr-2">
                        <span className={selectedProduct ? 'font-medium text-[#1e293b]' : ''}>
                          {selectedProduct ? selectedProduct.name : 'Search and select a product...'}
                        </span>
                        {selectedProduct?.code && (
                          <span className="text-xs text-slate-400 font-mono">({selectedProduct.code})</span>
                        )}
                        {selectedProduct && (
                          checkHasBOM(selectedProduct.id, selectedProduct.code) ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              BOM Created
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                              BOM Not Created
                            </span>
                          )
                        )}
                      </div>
                      {!isEditing && <i className="ri-search-line text-slate-400 shrink-0" />}
                    </div>
                    {showProductSearch && !isEditing && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                        <div className="p-2 border-b border-slate-200">
                          <input
                            autoFocus
                            type="text"
                            value={productSearchQuery}
                            onChange={(e) => setProductSearchQuery(e.target.value)}
                            placeholder="Search Finished Goods or Semi-Finished items..."
                            className="w-full h-9 px-3 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5]"
                          />
                        </div>
                        <div className="py-1">
                          {productDropdownData.parents.map((group) => (
                            <div key={group.id}>
                              <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50 uppercase tracking-wide">
                                {group.name}
                              </div>
                              {productDropdownData.variants
                                .filter((v) => v.parent_item_id === group.id)
                                .map((variant) => {
                                  const hasBom = checkHasBOM(variant.id, variant.code);
                                  return (
                                    <button
                                      key={variant.id}
                                      type="button"
                                      onClick={() => handleSelectProduct(variant.id, variant)}
                                      className="w-full px-6 py-2.5 text-left text-sm text-[#1e293b] hover:bg-indigo-50/80 cursor-pointer flex items-center gap-2 border-b border-slate-50 transition-colors"
                                    >
                                      <span className="text-slate-400">└</span>
                                      <span className="font-medium text-slate-800">{variant.name}</span>
                                      {variant.code && <span className="text-xs text-slate-400 font-mono">({variant.code})</span>}
                                      <div className="ml-auto flex items-center gap-2">
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                          Variant
                                        </span>
                                        {hasBom ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                            BOM Created
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                                            <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                            BOM Not Created
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                            </div>
                          ))}
                          {productDropdownData.regulars.map((item) => {
                            const hasBom = checkHasBOM(item.id, item.code);
                            const isSemi = item.item_type?.toUpperCase().includes('SEMI') || item.category?.toUpperCase().includes('SEMI');
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleSelectProduct(item.id, item)}
                                className="w-full px-3 py-2.5 text-left text-sm text-[#1e293b] hover:bg-indigo-50/80 cursor-pointer flex items-center gap-2 border-b border-slate-50 transition-colors"
                              >
                                <span className="font-medium text-slate-800">{item.name}</span>
                                {item.code && <span className="text-xs text-slate-400 font-mono">({item.code})</span>}
                                <div className="ml-auto flex items-center gap-2">
                                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${isSemi ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                                    {isSemi ? 'Semi-Finished' : 'Finished Good'}
                                  </span>
                                  {hasBom ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                      BOM Created
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                      BOM Not Created
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                          {productDropdownData.parents.length === 0 &&
                            productDropdownData.regulars.length === 0 && (
                              <div className="px-3 py-4 text-center text-sm text-slate-400">
                                {loadingItems ? 'Loading products...' : 'No finished goods or semi-finished items found'}
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* BOM Version */}
                  <div>
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      BOM Version <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedBOMId}
                      onChange={(e) => setSelectedBOMId(e.target.value)}
                      className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] cursor-pointer"
                    >
                      <option value="">Select BOM version...</option>
                      {availableBOMs.map((bom) => (
                        <option key={bom.id} value={bom.id}>
                          v{bom.version} ({bom.status}) — {bom.code}
                        </option>
                      ))}
                    </select>
                    {availableBOMs.length === 0 && selectedProduct && (
                      <p className="text-[11px] text-amber-600 mt-1">
                        No active BOMs found for this product
                      </p>
                    )}
                  </div>

                  {/* Routing */}
                  <div>
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      Routing{' '}
                      <span className="text-[#94a3b8] font-normal">(Optional)</span>
                    </label>
                    <SearchableSelect
                      value={selectedRoutingId}
                      onChange={setSelectedRoutingId}
                      options={routingOptions}
                      placeholder={
                        !selectedProductId
                          ? 'Select product first...'
                          : !effectiveWarehouseId
                            ? 'Select warehouse first...'
                            : 'Select routing...'
                      }
                      searchPlaceholder="Search routing..."
                      disabled={!selectedProductId || !effectiveWarehouseId}
                    />
                    {!selectedProductId ? (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Select product first to view linked routings
                      </p>
                    ) : !effectiveWarehouseId ? (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Select warehouse to view routings
                      </p>
                    ) : availableRoutings.length === 0 && !loadingRoutings ? (
                      <p className="text-[11px] text-amber-600 mt-1">
                        No active routings found for this item in selected warehouse
                      </p>
                    ) : loadingRoutings ? (
                      <p className="text-[11px] text-indigo-600 mt-1 flex items-center gap-1">
                        <i className="ri-loader-4-line animate-spin text-xs" />
                        Loading routings...
                      </p>
                    ) : null}
                    {selectedRouting && (
                      <p className="text-[11px] text-[#94a3b8] mt-1">
                        {(() => {
                          let stageCount = 0;
                          if (selectedRouting.stages) {
                            if (Array.isArray(selectedRouting.stages)) {
                              stageCount = selectedRouting.stages.length;
                            } else if (typeof selectedRouting.stages === 'string') {
                              try {
                                stageCount = JSON.parse(selectedRouting.stages).length;
                              } catch (e) {
                                stageCount = 0;
                              }
                            }
                          }
                          return stageCount;
                        })()} stages
                        {costPlan && Number(plannedQty) > 0
                          ? ` · ${costPlan.totals.plannedMinutes} min for ${plannedQty} ${unit || ''}`.trimEnd()
                          : `, total ${selectedRouting.total_time_minutes || 0} min`}
                      </p>
                    )}
                    {hasDraftStages && (
                      <div className="mt-2.5 p-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 text-xs flex items-start gap-2.5 shadow-2xs">
                        <i className="ri-error-warning-fill text-amber-500 text-base shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-semibold text-amber-900">
                            Incomplete BOM Mapping ({draftStages.length} stage{draftStages.length > 1 ? 's' : ''} in DRAFT)
                          </p>
                          <p className="mt-0.5 text-amber-700 leading-relaxed">
                            Stage <strong>{draftStages.map((s: any) => s.operationName || s.stageName || s.name || `Seq ${s.sequence || 1}`).join(', ')}</strong> is in DRAFT. Complete BOM mapping first before creating a Production Order.
                          </p>
                          <button
                            type="button"
                            onClick={() => navigate('/masters/routing')}
                            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-amber-900 bg-amber-100 hover:bg-amber-200 rounded border border-amber-300 transition-colors cursor-pointer"
                          >
                            <i className="ri-external-link-line" />
                            Open Routing Setup to Map BOM Materials &rarr;
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Qty to Produce */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      Qty to Produce <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 max-w-sm">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={plannedQty}
                        onChange={(e) => setPlannedQty(e.target.value)}
                        placeholder="Enter quantity"
                        className="flex-1 h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5]"
                      />
                      <div className="h-10 px-3 flex items-center text-sm text-[#64748b] bg-slate-50 border border-slate-200 rounded-lg min-w-[80px] justify-center">
                        {unit || '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                <h3 className="text-sm font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
                  <i className="ri-calendar-schedule-line text-[#4f46e5]" />
                  Schedule
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Priority */}
                  <div>
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as Priority)}
                      className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] cursor-pointer"
                    >
                      <option value="">Select priority...</option>
                      {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as Priority[]).map((p) => (
                        <option key={p} value={p}>{priorityConfig[p].label}</option>
                      ))}
                    </select>
                    {priority && priorityConfig[priority as Priority] && (
                      <div className="mt-1.5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border ${priorityConfig[priority as Priority].bg} ${priorityConfig[priority as Priority].text} ${priorityConfig[priority as Priority].border}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${priorityConfig[priority as Priority].dot}`} />
                          {priorityConfig[priority as Priority].label} Priority
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Shift */}
                  <div>
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      Shift <span className="text-[#94a3b8] font-normal">(Optional)</span>
                    </label>
                    <SearchableSelect
                      value={shiftId}
                      onChange={setShiftId}
                      options={shiftOptions}
                      placeholder="No shift assigned"
                      searchPlaceholder="Search shift..."
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      Planned Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={plannedStart}
                      onChange={(e) => setPlannedStart(e.target.value)}
                      className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5]"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                      Planned End Date <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={plannedEnd}
                        onChange={(e) => setPlannedEnd(e.target.value)}
                        className="flex-1 h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5]"
                      />
                      {selectedRouting && plannedStart && (
                        <span className="text-[11px] text-[#94a3b8] whitespace-nowrap">
                          Est: {formatShortDate(plannedStart)} +{' '}
                          {Math.ceil((selectedRouting.total_time_minutes || 0) / 60 / 8)} days
                        </span>
                      )}
                    </div>
                    {selectedRouting && (
                      <p className="text-[11px] text-[#94a3b8] mt-1">
                        Estimated based on routing. You can override.
                      </p>
                    )}
                  </div>
                </div>

                {selectedRoutingId && (
                  <ScheduleCapacityCheck
                    routingId={selectedRoutingId}
                    qty={Number(plannedQty) || 0}
                    plannedStart={plannedStart}
                    plannedEnd={plannedEnd}
                    warehouseId={effectiveWarehouseId || undefined}
                    excludeOrderId={isEditing ? editId : undefined}
                    unit={unit || undefined}
                    onApplyDates={(start, end) => {
                      if (start !== plannedStart) keepEndDateRef.current = true;
                      setPlannedStart(start);
                      setPlannedEnd(end);
                    }}
                  />
                )}
              </div>

              {/* Material Requirements Table */}
              {(selectedBOMId || materialRequirements.length > 0) && (
                <div className="bg-white border border-slate-200/90 rounded-2xl p-5 mb-5 shadow-xs">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-equalizer-line text-indigo-600 text-base" />
                    <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
                      Material Requirements for {plannedQty || '0'} {unit || selectedProduct?.unitName || 'Pcs'}
                    </h3>
                  </div>

                  {materialRequirements.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                      <i className="ri-inbox-line text-2xl mb-1 block text-slate-300" />
                      <p className="text-xs text-slate-500">
                        {loadingBOMDetails
                          ? 'Loading material requirements...'
                          : 'No components found for this BOM.'}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50/70 border-b border-slate-100">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                              Material
                            </th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                              Required
                            </th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                              Available
                            </th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                              Rate
                            </th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                              Amount
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide w-36">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/80">
                          {materialRequirements.map((mat) => (
                            <tr
                              key={mat.id || mat.itemId}
                              className="hover:bg-slate-50/50 transition-colors"
                            >
                              <td className="px-4 py-3.5">
                                <p className="text-sm font-semibold text-slate-800 leading-snug">
                                  {mat.itemName}
                                </p>
                                <p className="text-xs text-slate-400 font-normal mt-0.5">
                                  {mat.itemCode}
                                </p>
                              </td>
                              <td className="px-4 py-3.5 text-right">
                                <span className="text-sm text-slate-700 font-normal">
                                  {mat.requiredQty.toFixed(2)} {mat.unit}
                                </span>
                              </td>
                              <td className="px-4 py-3.5 text-right">
                                <span className="text-sm text-slate-700 font-normal">
                                  {mat.availableQty != null
                                    ? `${mat.availableQty} ${mat.unit}`
                                    : '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3.5 text-right text-sm text-slate-600">
                                {planRateByItem.has(String(mat.itemId)) ? `${money(planRateByItem.get(String(mat.itemId))!)}/${mat.unit}` : '—'}
                              </td>
                              <td className="px-4 py-3.5 text-right text-sm font-medium text-slate-800">
                                {planRateByItem.has(String(mat.itemId))
                                  ? money(mat.requiredQty * planRateByItem.get(String(mat.itemId))!)
                                  : '—'}
                              </td>
                              <td className="px-4 py-3.5 text-left">
                                {mat.isSufficient ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-emerald-200 bg-emerald-50 text-emerald-700">
                                    <span className="text-emerald-600 font-bold text-xs">✓</span>
                                    Sufficient
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-rose-200 bg-rose-50 text-rose-600">
                                    <span className="text-rose-600 font-bold text-xs">✕</span>
                                    Shortage
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {costPlan && costPlan.materials.length > 0 && (
                          <tfoot>
                            <tr className="border-t border-slate-200 bg-slate-50/70">
                              <td className="px-4 py-3 text-xs font-semibold text-slate-600" colSpan={4}>Material cost (standard)</td>
                              <td className="px-4 py-3 text-right text-sm font-bold text-slate-900">{money(costPlan.totals.material)}</td>
                              <td />
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Routing stages and standard cost for the quantity */}
              {(selectedBOMId || selectedRoutingId) && Number(plannedQty) > 0 && (
                <RoutingCostPlan
                  plan={costPlan}
                  loading={loadingCostPlan}
                  error={costPlanError}
                  hasRouting={!!selectedRoutingId}
                  unit={unit || selectedProduct?.unitName || 'Pcs'}
                />
              )}

              {/* Notes */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                <h3 className="text-sm font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
                  <i className="ri-sticky-note-line text-[#4f46e5]" />
                  Notes
                </h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Optional notes about this production order..."
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100 resize-none"
                />
              </div>

              {/* Status Workflow */}
              {isEditing && existingPO && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                  <h3 className="text-sm font-semibold text-[#1e293b] mb-3 flex items-center gap-2">
                    <i className="ri-toggle-line text-[#4f46e5]" />
                    Status Workflow
                  </h3>
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border ${statusConfig[currentStatus].bg} ${statusConfig[currentStatus].text} ${statusConfig[currentStatus].border}`}
                    >
                      <i className={statusConfig[currentStatus].icon} />
                      {statusConfig[currentStatus].label}
                    </span>
                    {statusOverride && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                        Will change on save
                      </span>
                    )}
                  </div>
                  {statusActions.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {statusActions.map((action) => (
                        <button
                          key={action.next}
                          onClick={() => handleStatusAction(action.next)}
                          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${action.variant}`}
                        >
                          <i className={action.icon} />
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Order Summary */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
                <h3 className="text-sm font-semibold text-[#1e293b] mb-3">Order Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Type', value: poType },
                    { label: 'Product', value: selectedProduct?.name || '—' },
                    { label: 'Quantity', value: plannedQty ? `${plannedQty} ${unit}` : '—' },
                    { label: 'Warehouse', value: selectedWarehouse?.name || '—' },
                    { label: 'Priority', value: priority && priorityConfig[priority as Priority] ? priorityConfig[priority as Priority].label : '—' },
                    { label: 'Start', value: plannedStart ? formatShortDate(plannedStart) : '—' },
                    { label: 'End', value: plannedEnd ? formatShortDate(plannedEnd) : '—' },
                    { label: 'BOM', value: selectedBOM ? `v${selectedBOM.version}` : '—' },
                    { label: 'Routing', value: selectedRouting ? `${selectedRouting.code} · ${costPlan?.stages.length ?? 0} stages` : '—' },
                    { label: 'Std Time', value: costPlan && selectedRouting ? `${costPlan.totals.plannedMinutes} min` : '—' },
                    { label: 'Std Cost', value: costPlan ? money(costPlan.totals.total) : '—' },
                    { label: 'Cost / Unit', value: costPlan ? money(costPlan.totals.perUnit) : '—' },
                  ].map((item) => (
                    <div key={item.label}>
                      <p className="text-xs text-[#64748b]">{item.label}</p>
                      <p className="text-sm font-medium text-[#1e293b] mt-0.5 truncate">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── MATERIALS TAB ── */}
          {activeTab === 'materials' && (
            <div className="bg-white border border-slate-200/90 rounded-2xl shadow-xs overflow-hidden mb-5">
              <div className="p-4 sm:p-5 flex items-center justify-between border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2.5">
                  <svg
                    className="w-5 h-5 text-indigo-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                  <h3 className="text-base font-bold text-slate-800 tracking-tight">
                    Material Reservations
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={handleReserveAll}
                  disabled={!isEditing || !editId || reservations.length === 0}
                  className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
                >
                  <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="3" x2="12" y2="15" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="5" y1="20" x2="19" y2="20" />
                  </svg>
                  Reserve All
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Item Name</th>
                      <th className="text-right px-6 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Required Qty</th>
                      <th className="text-right px-6 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Reserved Qty</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Unit</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Status</th>
                      <th className="text-right px-6 py-3.5 text-xs font-semibold text-slate-500 tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/80">
                    {reservations.map((mr) => {
                      const reqQty = Number(mr.requiredQty || 0);
                      const resQty = Number(mr.reservedQty || 0);
                      const isReserved = (mr.status === 'RESERVED' || (resQty >= reqQty && reqQty > 0)) && resQty > 0;
                      const isPartial = (mr.status === 'PARTIAL' || (resQty > 0 && resQty < reqQty)) && !isReserved;
                      const isPending = !isReserved && !isPartial;

                      return (
                        <tr key={mr.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-slate-800">{mr.itemName}</div>
                            <div className="text-xs text-slate-400 font-normal mt-0.5">{mr.itemCode}</div>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-slate-600 font-normal">
                            {Number(mr.requiredQty || 0).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-slate-600 font-normal">
                            {Number(mr.reservedQty || 0).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-left text-sm text-slate-600 font-normal">
                            {mr.unit}
                          </td>
                          <td className="px-6 py-4 text-left">
                            {isReserved && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-emerald-300 bg-emerald-50 text-emerald-700">
                                <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Reserved
                              </span>
                            )}
                            {isPartial && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-amber-300 bg-amber-50 text-amber-700">
                                <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                                  <line x1="12" y1="9" x2="12" y2="13" />
                                  <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                Partial
                              </span>
                            )}
                            {isPending && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-rose-300 bg-rose-50 text-rose-600">
                                <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {(isPending || isPartial) && (
                                <button
                                  type="button"
                                  onClick={() => handleReserveOne(mr)}
                                  className="h-8 px-3 rounded-lg border border-emerald-300 bg-emerald-50/70 hover:bg-emerald-100/80 text-emerald-700 text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
                                >
                                  <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <line x1="12" y1="5" x2="12" y2="17" />
                                    <polyline points="7 12 12 17 17 12" />
                                    <line x1="5" y1="20" x2="19" y2="20" />
                                  </svg>
                                  Reserve
                                </button>
                              )}
                              {(isReserved || isPartial) && (
                                <button
                                  type="button"
                                  onClick={() => handleReleaseOne(mr)}
                                  className="h-8 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
                                >
                                  <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <line x1="12" y1="19" x2="12" y2="7" />
                                    <polyline points="7 12 12 7 17 12" />
                                    <line x1="5" y1="4" x2="19" y2="4" />
                                  </svg>
                                  Release
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {reservations.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-3">
                            <i className="ri-inbox-line text-xl text-slate-300" />
                          </div>
                          <p className="text-sm font-medium text-slate-600">No material reservations yet.</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {!isEditing
                              ? 'Save the production order to generate and manage material reservations.'
                              : 'Click Reserve All to allocate inventory for this production order.'}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MRP Impact Banner */}
      {showMRPBanner && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <div className="flex items-center justify-between bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 shadow-lg">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
                <i className="ri-bar-chart-2-line text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  Production Order affects material requirements
                </p>
                <p className="text-xs text-amber-600">
                  Run MRP to recalculate purchase suggestions for this order.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate('/manufacturing/mrp')}
                className="h-8 px-3 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 cursor-pointer whitespace-nowrap transition-colors"
              >
                Run MRP Now
              </button>
              <button
                onClick={() => {
                  setShowMRPBanner(false);
                  navigate('/manufacturing/production-orders');
                }}
                className="h-8 px-3 rounded-lg border border-amber-300 text-amber-700 text-xs font-medium hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {(showProductSearch || showSalesOrderSearch) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowProductSearch(false);
            setShowSalesOrderSearch(false);
          }}
        />
      )}

      {showPrint && isEditing && existingPO && (
        <POPrintModal po={existingPO} onClose={() => setShowPrint(false)} />
      )}

      <ConfirmDialog
        open={showCancelConfirm}
        title="Cancel Production Order"
        message="Are you sure you want to cancel this production order? This action cannot be undone."
        variant="danger"
        confirmLabel="Cancel Order"
        cancelLabel="Keep Order"
        onConfirm={() => {
          setStatusOverride('CANCELLED');
          setShowCancelConfirm(false);
          toast.info('Status will change to Cancelled on save');
        }}
        onCancel={() => setShowCancelConfirm(false)}
      />

      {/* ── Order Confirmation Summary Modal ── */}
      <OrderConfirmationSummaryModal
        summary={createdSummary}
        onClose={() => {
          const poId = createdSummary?.prd_id || createdSummary?.data?.id;
          setCreatedSummary(null);
          if (poId) {
            navigate(`/manufacturing/production-orders/${poId}`);
          } else {
            navigate('/manufacturing/production-orders');
          }
        }}
        onViewOrderDetails={(poId) => {
          setCreatedSummary(null);
          navigate(`/manufacturing/production-orders/${poId}`);
        }}
      />
    </AppLayout>
  );
}